import { Investorstatus } from '../../../login/redux/types';
import { Audit, InvestmentDoc } from '../../Investments/redux/types';

const DealTypes = {
    GET_DEALS_REQ: 'GET_DEALS_REQ',
    SET_DEALS: 'SET_DEALS',
    GET_DEAL_DETAIL_REQ: 'GET_DEAL_DETAIL_REQ',
    SET_DEAL_DETAIL: 'SET_DEAL_DETAIL',
    GET_DEAL_DATA_ROOM_REQ: 'GET_DEAL_DATA_ROOM_REQ',
    SET_DEAL_DATA_ROOM: 'SET_DEAL_DATA_ROOM',
    GET_DEAL_WEBINAR_REQ: 'GET_DEAL_WEBINAR_REQ',
    SET_DEAL_WEBINAR: 'SET_DEAL_WEBINAR',
    SET_DISCLOSURE: 'SET_DISCLOSURE',
    //Track Records
    FETCH_TRACK_RECORDS: 'FETCH_TRACK_RECORDS',
    SET_TRACK_RECORDS: 'SET_TRACK_RECORDS',
    //Cashflow Simulator
    FETCH_CASHFLOW_SIMULATOR: 'FETCH_CASHFLOW_SIMULATOR',
    SET_CASHFLOW_SIMULATOR: 'SET_CASHFLOW_SIMULATOR',
    //Term sheet
    GET_TERM_SHEET_DATA_REQ: 'GET_TERM_SHEET_DATA_REQ',
    SET_TERM_SHEET_DATA: 'SET_TERM_SHEET_DATA',
    // Teaser
    FETCH_TEASER_DOC: 'FETCH_TEASER_DOC',
    SET_TEASER_DOC: 'SET_TEASER_DOC',
};

export default DealTypes;

export interface Amount {
    amountUpto2dp?: number;
    currency: string;
}

export interface BankAccount {
    accountCurrency: string;
    accountName: string;
    accountNumber: string;
    bankAccountUUID: string;
    beneficiaryBank: {
        bankCode: string;
        bankName: string;
        branchCode?: string;
        swiftCode: string;
        address?: {
            address1?: string;
            address2?: string;
            country?: string;
            postalCode?: string;
        };
    };
    intermediaryBank: {
        bankCode: string;
        bankName: string;
        branchCode?: string;
        swiftCode: string;
        address?: {
            address1?: string;
            address2?: string;
            country?: string;
            postalCode?: string;
        };
    };
}

export interface Performance {
    audit?: Audit;
    cumulativeRate?: number;
    isInvestmentOverride: boolean;
    month?: number;
    rate?: number;
    year?: number;
}

export interface FundPerformance {
    comment: string;
    effectiveDate?: {
        date: string;
    };
    performances: Performance[];
}

export interface AllocationInPercent {
    type: string;
    allocations: {
        key: string;
        value: number;
    }[];
}

export interface Allocation {
    effectiveDate?: {
        date: string;
    };
    allocationInPercent?: AllocationInPercent[];
}

export interface PPM {
    investorUUID: string;
    ppmNumber: number;
    ppmUrl: string;
    timestamp: string;
}

export interface FundInfo {
    administrator: string;
    auditor: string;
    baseCcy: string;
    benchmarkUUID: string;
    domicile: string;
    eligibleInvestors: string;
    fees?: {
        managementFee: number;
        performanceFee: number;
    };
    fundManager: string;
    fundMetrics?: {
        amountDisbursedInMillions?: Amount;
        annualNetReturnInPercent: number;
        avgLoanSize?: Amount;
        effectiveDate?: {
            date: string;
        };
        lastMonthNAVGrowthInPercent: number;
        outstandingLoanCount: number;
        weightedAvgMaturityInMonths: number;
    };
    geography: string;
    instrument: string;
    legalCounsel: string;
    lockInPeriodInMonths: 0;
    sector: string;
    taxAdvisor: string;
}

export interface CashFlowSimulator {
    businessDayConvention: string;
    currency: string;
    dayCountConvention: string;
    durationInMonths: number;
    frequency: string;
    holidayCalendars: string[];
    interestRate: number;
    repaymentType: 'Bullet' | 'Amortisation';
    stubType: string;
    showInInvestorPortal?: boolean;
}

export interface Tag {
    uuid: string;
    name: string;
    creationAudit?: Audit;
    audit?: Audit;
}
export interface Deal {
    cashFlowSimulator?: CashFlowSimulator;
    image?: string;
    BankAccount?: BankAccount;
    documents?: {
        dataRoom?: InvestmentDoc[];
        executedLoanAgreement: string;
        updates?: InvestmentDoc[];
    };
    downloadAllowed?: string[];
    fundDetails?: {
        distributionComment?: string;
        allocation?: Allocation;
        fundPerformance: FundPerformance;
        info: FundInfo;
        ppms?: PPM[];
    };
    dealTags?: {
        tags?: Tag[];
        headlinerTags?: Tag[];
    };
    info: {
        details?: {
            key: string;
            value: string[] | undefined;
        }[];
        isPublic: boolean;
        issuerUUID: string;
        summary?: {
            currencies?: string[];
            dealType?: string;
            description: string;
            endTime?: string;
            fundingCommitted?: Amount;
            instrumentType?: string;
            minimumInvestment?: Amount[];
            startTime: string;
            targetFundingSize?: Amount;
            title: string;
            terms?: {
                distribution: string;
                tenorInMonths: number;
                equityFundingToDate?: Amount;
                fundingRound?: string;
                interestRates?: {
                    ccy: string;
                    interestRate: string;
                }[];
            };
        };
    };
    status: string;
    uuid: string;
}

export interface Issuer {
    category: string;
    country: string;
    description?: string;
    issuerUUID?: string;
    logo?: string;
    name?: string;
    status?: string;
}

export interface Team {
    uuid: string;
    imageURL: string;
    name: string;
    role: string;
}

export interface FAQ {
    answer: string;
    index: number;
    question: string;
    uuid: string;
}

export interface FreeText {
    index: number;
    uuid: string;
    text: string;
}
export interface Deals {
    deal: Deal;
    investorStatus?: Investorstatus;
    issuer?: Issuer;
    team?: Team[];
    faq?: FAQ[];
    freeText?: FreeText[];
}

export interface TrackRecord {
    benchmarkCRate: number;
    benchmarkRate: number;
    month: number;
    cRate: number;
    rate: number;
    year: number;
    quarter: string;
}

export interface TrackRecords {
    effectiveDate?: {
        date: string;
    };
    benchmarkName: string;
    comment?: string;
    performance: TrackRecord[];
}

export interface DataRoomChildren {
    dealUUID: string;
    group: 'DataRoom' | 'ExecutedAgreements' | 'Updates' | 'PPM';
    isDownloadable: boolean;
    name: string;
    parentUUID?: string;
    type: 'Folder' | 'File';
    uuid: string;
    investmentUUID?: string;
    children?: DataRoomChildren[];
}

export interface DataRoom {
    audit?: Audit;
    dealUUID: string;
    group: 'DataRoom' | 'ExecutedAgreements' | 'Updates' | 'PPM';
    isDownloadable: boolean;
    name: string;
    type: 'Folder' | 'File';
    uuid: string;
    investmentUUID?: string;
    parentUUID?: string;
    children?: DataRoomChildren[];
}

export interface Webinar {
    dealUUID: string;
    date: {
        date: string;
    };
    description: string;
    title: string;
    url: string;
    uuid: string;
}

export interface Cashflow {
    interestPayment?: Amount;
    paymentDate?: {
        date: string;
    };
    principalOutstanding?: Amount;
    principalRepayment?: Amount;
    totalAmount?: Amount;
    adminFee?: Amount;
    netPayment?: Amount;
    typeUUID?: string;
    type: 'Booking' | 'Investment';
    uuid: string;
    managementFee?: Amount;
    totalHSFee?: Amount;
    borrowerFee?: Amount;
}

export interface Cashflowgraph {
    interestPayment: number;
    principalPayment: number;
    date: string;
}

export interface DealState {
    deals: Deals[];
    pastDeals: Deals[];
    ongoingDeals: Deals[];
    loading: boolean;
    deal: Deals | undefined;
    dataRoomLoading: boolean;
    dataRoom: DataRoom[];
    webinarLoading: boolean;
    webinars: Webinar[];
    disclosure: boolean;
    trackRecords?: TrackRecords;
    trackRecordLoading: boolean;
    cashflowGraph: Cashflowgraph[];
    cLoading: boolean;
    termSheetData?: string;
    tsLoading: boolean;
    teaser: DataRoom[];
    tLoading: boolean;
}
